import type { WhatsPossibleAtMaturityMetric } from '@act/shared/data-transfer-objects';
import {
  WHATS_POSSIBLE_AT_MATURITY_OVERLINE,
  WHATS_POSSIBLE_AT_MATURITY_SUMMARY,
  buildWhatsPossibleAtMaturityRoiSubtitle,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { body, CORAL, display } from './tokens';

const METRIC_PANEL_STYLE = {
  padding: '28px 32px',
  borderRadius: 18,
  background: 'rgba(255,123,101,0.08)',
  border: '1px solid rgba(255,123,101,0.25)',
  alignItems: 'center',
} as const;

const readMetrics = (data: Record<string, unknown>): WhatsPossibleAtMaturityMetric[] =>
  Array.isArray(data.metrics) ? (data.metrics as WhatsPossibleAtMaturityMetric[]) : [];

interface MetricColumnProps {
  metric: WhatsPossibleAtMaturityMetric;
  customerName: string;
  showDivider?: boolean;
}

/**
 * Renders one benchmark metric column in the maturity results panel.
 * @param props - Metric data and whether to show a left divider.
 */
const MetricColumn = ({ metric, customerName, showDivider = false }: MetricColumnProps) => {
  if (metric.type === 'roi') {
    return (
      <div className="report-whats-possible-maturity-section__metric-column">
        <div
          className="overline"
          style={{ color: CORAL, marginBottom: 8, display: 'block' }}
        >
          {metric.label}
        </div>
        <div
          className="report-whats-possible-maturity-section__metric-value report-whats-possible-maturity-section__metric-value--roi"
          style={{
            fontFamily: display,
            fontSize: 28,
            color: 'var(--white)',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {metric.value}
          {metric.subtitle ? (
            <>
              <br />
              <span
                className="report-whats-possible-maturity-section__roi-subtitle"
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 14,
                  fontWeight: 400,
                  fontFamily: body,
                }}
              >
                achieved in{' '}
                <span className="report-whats-possible-maturity-section__customer-name report-whats-possible-maturity-section__customer-name--inline">
                  {customerName}
                </span>
              </span>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        showDivider
          ? 'report-whats-possible-maturity-section__metric-column report-whats-possible-maturity-section__metric-column--divided'
          : 'report-whats-possible-maturity-section__metric-column'
      }
    >
      <div
        className={`report-whats-possible-maturity-section__metric-value${
          metric.type === 'currency'
            ? ' report-whats-possible-maturity-section__metric-value--currency'
            : ''
        }`}
        style={{
          fontFamily: display,
          fontWeight: 600,
          fontSize: '2.25rem',
          lineHeight: 1,
          color:
            metric.type === 'currency'
              ? 'var(--reema-coral-100)'
              : 'var(--white)',
        }}
      >
        {metric.value}
      </div>
      <div
        className="report-whats-possible-maturity-section__metric-label"
        style={{
          fontFamily: body,
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.55)',
          marginTop: 8,
        }}
      >
        {metric.label}
      </div>
    </div>
  );
};

/**
 * What's Possible at Maturity: static OBH Wisconsin benchmark results on midnight Studio layout.
 * @param props - Resolved section snapshot and customer context.
 */
const WhatsPossibleAtMaturity = ({ data, customer }: ReportHostedSectionProps) => {
  const metrics = readMetrics(data).map(metric =>
    metric.type === 'roi'
      ? {
          ...metric,
          subtitle: buildWhatsPossibleAtMaturityRoiSubtitle(customer.name),
        }
      : metric,
  );
  const overline =
    typeof data.overline === 'string'
      ? data.overline
      : WHATS_POSSIBLE_AT_MATURITY_OVERLINE;
  const summary =
    typeof data.summary === 'string'
      ? data.summary
      : WHATS_POSSIBLE_AT_MATURITY_SUMMARY;

  return (
    <HostedReportSectionLayout
      sectionDomId="whats-possible-at-maturity"
      className="section--midnight report-hosted-section-body report-whats-possible-maturity-section"
      style={{ background: 'var(--reema-midnight-100)', fontFamily: body }}
      innerClassName="container report-whats-possible-maturity-section__inner"
      banner={null}
    >
      <div className="report-whats-possible-maturity-section__body">
        <SectionReveal>
          <div
            className="report-whats-possible-maturity-section__intro"
            style={{ marginBottom: '2rem' }}
          >
            <span
              className="overline"
              style={{
                color: CORAL,
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              {overline}
            </span>
            <h2
              className="heading-xl report-whats-possible-maturity-section__headline"
              style={{
                color: 'var(--white)',
                maxWidth: '48rem',
                marginBottom: '1.5rem',
                fontFamily: display,
                fontWeight: 600,
                lineHeight: 1.12,
                letterSpacing: '-0.01em',
              }}
            >
              Validated results from{' '}
              <span className="report-whats-possible-maturity-section__customer-name">
                {customer.name}
              </span>
              .
            </h2>
            <p
              className="report-whats-possible-maturity-section__summary"
              style={{
                fontFamily: body,
                fontSize: '1.125rem',
                lineHeight: 1.55,
                color: 'var(--reema-sand-100)',
                maxWidth: '48rem',
                margin: 0,
              }}
            >
              {summary}
            </p>
          </div>

          <div
            className="report-whats-possible-maturity-section__metrics"
            style={METRIC_PANEL_STYLE}
          >
            {metrics.map((metric, index) => (
              <MetricColumn
                key={metric.label}
                metric={metric}
                customerName={customer.name}
                showDivider={index > 0}
              />
            ))}
          </div>

          <p
            className="report-whats-possible-maturity-section__source"
            style={{
              fontFamily: body,
              fontSize: '0.875rem',
              fontStyle: 'italic',
              color: 'rgba(255,255,255,0.6)',
              marginTop: '1.5rem',
              marginBottom: 0,
            }}
          >
            Source: Reema&apos;s actuarial analysis of the{' '}
            <span className="report-whats-possible-maturity-section__customer-name report-whats-possible-maturity-section__customer-name--inline">
              {customer.name}
            </span>{' '}
            contract.
          </p>
        </SectionReveal>
      </div>
    </HostedReportSectionLayout>
  );
};

export default WhatsPossibleAtMaturity;
